import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EnvService } from "src/app/services/env.service";
import { DpGetEnums, DpGetEnumsSuccess, DpSendCommit, DpSendCommitSuccess, DpSendValidate, DpSendValidateSuccess, GetBankAccounts, GetBankAccountsSuccess, GetBankGFFPFields, GetBankGFFPFieldsSuccess, GetPaymentPlatformMetaData, GetPaymentPlatformMetaDataSuccess, SaveNewBankAccount } from "./digital-payment.action";
import { catchError, map, mergeMap, of } from "rxjs";
import { langSpecificEnums } from "../utils";
import { Labels } from "src/app/app.models";
import { GFFP_FIELDS_CATEGORY } from "src/app/gffp/gffp.models";
import { BankAccountService } from "src/app/services/bank-account.service";
import { GetBankAccountsResponse, GetBankGFFPFieldsResponse, GetBankGFFPParams } from "src/app/feature/digital-payment/bank-account/bank-account.models";
import { SendValidateResponse } from "../fee-quote";

@Injectable()
export class DigitalPaymentEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private envService: EnvService,
    private bankAccountService: BankAccountService,
    private store: Store
  ) {}

  
  private enumUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
    ? `${this.envService.apiUrl}/v3/reference-data/enumerations?`
    : `${this.envService.apiUrl}/v2/reference-data/enumerations?`;
  }
  
  private sendValidateUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
    ? `${this.envService.apiUrl}/v3/transactions/`
      : `${this.envService.apiUrl}/v2/transactions/`;
  }

  public getBankAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetBankAccounts),
      mergeMap(({ digitalProfileId, firebaseAuthToken}) => {
        return this.bankAccountService.getBankAccounts(digitalProfileId, firebaseAuthToken)
          .pipe(
            map((res: GetBankAccountsResponse) => {
              if (!!res && res?.bankAccounts && res.bankAccounts.length > 0) {
                return GetBankAccountsSuccess(res)
              }
              else {
                return GetBankAccountsSuccess({
                  bankAccounts: []
                })
              }
            })
            // catchError((error) => { // Todo: Tim: add error handling #may6
            //   return GetBankAccountsFailed
            // })
          )
      })
    )
  )

  public getBankGFFPFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetBankGFFPFields),
      mergeMap((urlParams: GetBankGFFPParams) => {
        return this.bankAccountService.getBankGFFPFields(urlParams)
          .pipe(
            map((res: GetBankGFFPFieldsResponse) => {
              return GetBankGFFPFieldsSuccess(res)
            })
            // catchError((error) => { // Todo: Tim: add error handling #may6
            //   return GetBankGFFPFieldsFailed
            // })
          )
      })
    )
  )

  public dpGetEnumerations$ = createEffect(() => 
    this.actions$.pipe(
      ofType(DpGetEnums),
      mergeMap(({getEnumRequest, endpoint, sendValidatePayload, transactionId, operation, isNewBankAccount, fbAuthToken, cmsLabels}) => {
        return this.http.get(`${this.enumUrl(endpoint)}userLanguage=${getEnumRequest.userLanguage}&targetAudience=CUSTOMER_FACING&transactingCountryCode=${getEnumRequest.countryCode}`)
          .pipe(
            map((getEnumsResponse: any) => {
              const labels: Labels = cmsLabels;
              const enumsObj = labels?.stellar?.enumsObj;

              if (getEnumsResponse && getEnumsResponse.length > 0 && enumsObj) {
                getEnumsResponse = langSpecificEnums(getEnumsResponse, enumsObj);
              }

              return DpGetEnumsSuccess({ getEnumsResponse, sendValidatePayload, transactionId, operation, isNewBankAccount, endpoint, fbAuthToken})
            }),
            // catchError((error) => of(GetEnumsFailed({ error })))
          )
      })
    )
  )
  
  public dpSendValidate$ = createEffect(() => 
    this.actions$.pipe(
      ofType(
        DpGetEnumsSuccess,
        DpSendValidate
      ),
      mergeMap(({sendValidatePayload, transactionId, endpoint, operation, isNewBankAccount, fbAuthToken}) => {
        return this.http.put(`${this.sendValidateUrl(endpoint)}${transactionId}?operation=${operation}`, sendValidatePayload)
        .pipe(
          map((data: any) => { // Todo: fix type
            if (sendValidatePayload['destinationCountryCode'] === 'HUN') {
              data = this.checkForHungaryFields(data)
            }
            if (data?.targetAccountProfileId && isNewBankAccount) {
              return SaveNewBankAccount({ sendValidateResponse: data, sendValidatePayload, fbAuthToken })
            }
            else {
              return DpSendValidateSuccess({ sendValidateResponse: data })
            }
          }),
            // catchError((error) => of(SendValidateFailed({ error })))
        )
      })
    )
  )

  public saveNewBankAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SaveNewBankAccount
      ),
      mergeMap(({ sendValidateResponse, sendValidatePayload, fbAuthToken }) => {
        return this.bankAccountService.saveNewBankAccount(sendValidateResponse, sendValidatePayload, fbAuthToken)
          .pipe(
            map((res) => {
              return DpSendValidateSuccess({ sendValidateResponse })
            }),
            // catchError((error) => of(SendValidateFailed({ error })))
          )
      })
    )
  )

  public getPaymentPlatformMetaData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GetPaymentPlatformMetaData
      ),
      mergeMap(({payload, fbAuthToken}) => {
        const headers = new HttpHeaders({
          'X-Firebase-Auth': fbAuthToken
        })
        return this.http.post(`${this.envService.apiUrl}/v2/payment/initiate`, { ...payload }, { headers })
          .pipe(
            map((data) => {
              return GetPaymentPlatformMetaDataSuccess({ getPaymentPlatformMetaDataResponse: data })
            }),
            // catchError((error) => of(SendValidateFailed({ error })))
          )
      })
    )
  )
  
  public dpSendCommit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DpSendCommit
      ),
      mergeMap(({ transactionId, locale, endpoint }) => {
        return this.http.post(`${this.sendValidateUrl(endpoint)}${transactionId}/commit`, { userLanguage: locale})
          .pipe(
            map((sendCommitResponse) => {
              return DpSendCommitSuccess({ sendCommitResponse })
            }),
            // catchError((error) => of(SendCommitFailed({ error })))
          )
      })
    )
  )
  
  // Todo: Tim: Copy/paste from transaction.effcts, could be refactored
  checkForHungaryFields(data: any) {
    if (
      data &&
      !!data?.errors &&
      data?.errors[0]?.category && 
      GFFP_FIELDS_CATEGORY.includes(data?.errors[0]?.category)
    ) {
      const isMaidenNameExists = data.errors
        .find((error: any) => error.category && GFFP_FIELDS_CATEGORY.includes(error.category))
        ?.offendingFields?.some(
          (fieldObj: { field: any }) =>
            fieldObj.field === 'sender.additionalFamilyNames.mothersMaidenName'
        );
      if (!isMaidenNameExists) {
        data.errors[0].offendingFields.push({
          field: 'sender.additionalFamilyNames.mothersMaidenName',
        });
      }
    }
    return data;
  }
} 
